/**
 * Tendina con ricerca per scegliere i giocatori di un grafico, comune a
 * Carichi (V68) e Confronto (V31): i gruppi per ruolo in cima, poi i
 * giocatori divisi per ruolo da spuntare uno a uno, ciascuno con il pallino
 * del colore della sua serie.
 */

import { Fragment, useState } from 'react';
import { RUOLI_GIOCATORE, type RuoloGiocatore } from '@calcio/shared';
import { ChevronsUpDownIcon, UsersIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { senzaTastieraSuTouch } from './focus.js';
import {
  ETICHETTA_PRESET,
  PRESET_GIOCATORI,
  type PresetGiocatori,
  idsDelPreset,
} from '../carichi/serieSquadra.js';

export interface GiocatoreScelta {
  id: string;
  nome: string;
  cognome: string;
  ruolo: RuoloGiocatore;
}

function presetAttivo(
  scelti: readonly string[],
  rosa: readonly GiocatoreScelta[],
): PresetGiocatori | '' {
  if (scelti.length === 0) return '';
  const set = new Set(scelti);
  for (const p of PRESET_GIOCATORI) {
    const ids = idsDelPreset(p, rosa);
    if (ids.length === set.size && ids.every((id) => set.has(id))) return p;
  }
  return '';
}

function etichettaScelta(
  scelti: readonly string[],
  rosa: readonly GiocatoreScelta[],
  preset: PresetGiocatori | '',
): string {
  if (preset) return ETICHETTA_PRESET[preset];
  if (scelti.length === 0) return 'Nessuno';
  if (scelti.length === 1) {
    const g = rosa.find((x) => x.id === scelti[0]);
    if (g) return `${g.cognome} ${g.nome}`;
  }
  return `${scelti.length} giocatori`;
}

/** Cerca per pezzo di nome, non fuzzy: il valore è «nome|id». */
function filtroNome(valore: string, cerca: string): number {
  const nome = valore.split('|')[0] ?? '';
  return nome.toLowerCase().includes(cerca.trim().toLowerCase()) ? 1 : 0;
}

export function SceltaGiocatori({
  id,
  rosa,
  scelti,
  colore,
  onCambia,
}: {
  id: string;
  rosa: readonly GiocatoreScelta[];
  scelti: readonly string[];
  /** Il colore della serie del giocatore; senza, il pallino resta vuoto. */
  colore: (id: string) => string | undefined;
  onCambia: (ids: string[]) => void;
}) {
  const [aperto, setAperto] = useState(false);
  const preset = presetAttivo(scelti, rosa);
  const scambia = (gid: string) =>
    onCambia(scelti.includes(gid) ? scelti.filter((x) => x !== gid) : [...scelti, gid]);

  return (
    <Popover open={aperto} onOpenChange={setAperto}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={aperto}
          className="justify-between font-normal"
        >
          <UsersIcon data-icon="inline-start" />
          {etichettaScelta(scelti, rosa, preset)}
          <ChevronsUpDownIcon data-icon="inline-end" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" onOpenAutoFocus={senzaTastieraSuTouch}>
        <Command filter={filtroNome}>
          <CommandInput placeholder="Cerca giocatore…" />
          <CommandList>
            <CommandEmpty>Nessun giocatore trovato.</CommandEmpty>
            <CommandGroup heading="Gruppi">
              {PRESET_GIOCATORI.map((p) => (
                <CommandItem
                  key={p}
                  value={ETICHETTA_PRESET[p]}
                  data-checked={preset === p}
                  onSelect={() => onCambia(idsDelPreset(p, rosa))}
                >
                  {ETICHETTA_PRESET[p]}
                </CommandItem>
              ))}
              <CommandItem
                value="Nessuno"
                data-checked={scelti.length === 0}
                onSelect={() => onCambia([])}
              >
                Nessuno
              </CommandItem>
            </CommandGroup>
            {RUOLI_GIOCATORE.map((ruolo) => {
              const delRuolo = rosa.filter((g) => g.ruolo === ruolo);
              if (delRuolo.length === 0) return null;
              return (
                <Fragment key={ruolo}>
                  <CommandSeparator />
                  <CommandGroup heading={ETICHETTA_PRESET[ruolo]}>
                    {delRuolo.map((g) => {
                      const c = colore(g.id);
                      return (
                        <CommandItem
                          key={g.id}
                          value={`${g.cognome} ${g.nome}|${g.id}`}
                          data-checked={scelti.includes(g.id)}
                          onSelect={() => scambia(g.id)}
                        >
                          <span
                            aria-hidden
                            className="size-2.5 shrink-0 rounded-full border border-border"
                            style={c ? { background: c, borderColor: c } : undefined}
                          />
                          {g.cognome} {g.nome}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </Fragment>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
